/**
 * useStickyTopOffset
 *
 * Publishes a `--slh-sticky-top-offset` CSS variable on <html> that adds up
 * the height of every currently-mounted `[data-slh-top-banner]` element,
 * plus the default 12 px breathing room used by the sticky header CTA.
 *
 * Any future admin bar, deployment banner or in-app system notification
 * only needs to add `data-slh-top-banner` to its outermost element and
 * this hook nudges the sticky "List your Home" CTA (and any other
 * consumer of the variable) below it automatically.
 *
 * Watches the DOM via a `MutationObserver` + `ResizeObserver` so the
 * offset stays live if banners appear, resize or disappear.
 */
import { useEffect } from "react";

const BASE_PX = 12;

function computeOffset() {
  if (typeof document === "undefined") return BASE_PX;
  const banners = document.querySelectorAll("[data-slh-top-banner]");
  let total = 0;
  banners.forEach((el) => {
    // Only count elements that actually take up screen space at the top —
    // ignore hidden banners or ones the CSS has already collapsed.
    const rect = el.getBoundingClientRect();
    if (rect.height > 0 && rect.top <= BASE_PX) total += rect.height;
  });
  return BASE_PX + total;
}

export default function useStickyTopOffset() {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const root = document.documentElement;
    const sync = () => {
      root.style.setProperty("--slh-sticky-top-offset", `${computeOffset()}px`);
    };
    sync();
    // MutationObserver catches banners being added / removed anywhere in
    // the tree. ResizeObserver catches an existing banner growing or
    // shrinking (e.g. multi-line notification wrapping on mobile).
    const mo = new MutationObserver(sync);
    mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["data-slh-top-banner", "hidden", "style", "class"] });
    let ro = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(sync);
      document.querySelectorAll("[data-slh-top-banner]").forEach((el) => ro.observe(el));
    }
    window.addEventListener("resize", sync);
    return () => {
      mo.disconnect();
      if (ro) ro.disconnect();
      window.removeEventListener("resize", sync);
    };
  }, []);
}
